import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { Heading } from '@mm/components';
import { Overlay } from '@mm/components/src/Overlay/Overlay';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import { TagIconBackground } from '@mm/icons';
import { useAutomationsCRUDActions } from '@mm/state/actions/automationActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { getIsAutomationTemplateFeatureEnabled } from '@mm/state/selectors/automations';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { companyFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import {
  activeChatbotIdSelector,
  getTwilioAvailable,
  selectIsShopifyContext,
} from '@mm/state/selectors/viewSelectors';
import { assertUnreachable } from '@mm/utils';
import { ReactComponent as WebchatIcon } from 'assets/webchat-new.svg';
import classNames from 'classnames';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { BotBuilderRoutes } from '../../../../../../../routes/bot-builder-routes';
import { useChannelConnection } from '../../AutomationEdit/InputTriggers/use-channel-connection';
import { useAutomations } from '../../use-automations';
import { ActiveAutomation } from './ActiveAutomation';
import { AutomationTemplateContainer } from './AutomationTemplateContainer';
import { AutomationTemplates } from './AutomationTemplates';
import { ReactComponent as UploadIcon } from './csv_upload_outreach_illustration.svg';
import { ReactComponent as FacebookIcon } from './facebook.svg';
import { ReactComponent as GmailIcon } from './gmail.svg';
import { ReactComponent as InstagramIcon } from './instagram.svg';

import styles from './TemplateGoButton.module.css';
import {
  AUTOMATION_TEMPLATES,
  AutomationTemplateChannel,
  AutomationTemplateName,
} from './Templates/TemplateDefinitions';
import XrayIconSrc from './xray_outreach_illustration.svg';

interface Props {
  templateName?: AutomationTemplateName;
  type?: 'hero' | 'highlight' | 'regular';
  label?: string;
  css?: {
    container?: string;
    button: string;
  };
  leftIcon?: React.ReactNode;
  onClick?: () => void;
}

export const DEFAULT_TEMPLATE_NAME = 'ultimate_welcome_funnel';

export const IG_AUTOMATIONS_SIGNUP_GA_EVENT = 'ig_automations_signup';

export const ChannelInfo = (props: {
  channel: AutomationTemplateChannel;
  width?: number;
  height?: number;
}) => {
  const { channel, width = 28, height = 28 } = props;

  switch (channel) {
    case 'instagram':
      return (
        <>
          <InstagramIcon width={width} height={height} />
          Instagram
        </>
      );
    case 'facebook':
      return (
        <>
          <FacebookIcon width={width} height={height} /> Facebook
        </>
      );
    case 'email':
      return (
        <>
          <GmailIcon width={width} height={height} /> Email
        </>
      );
    case 'webchat':
      return (
        <>
          <WebchatIcon width={width} height={height} /> Web Chat
        </>
      );
    default:
      assertUnreachable(channel);
      return null;
  }
};
const DialogItem = React.memo(
  (props: {
    iconElement: any;
    name: string;
    description: string;
    isConnected?: boolean;
    onClick: () => void;
    disableReason?: string;
    width?: number;
    height?: number;
    tagLabel?: string;
    tagColor?: string;
  }) => {
    const {
      iconElement,
      name,
      description,
      onClick,
      disableReason,
      isConnected,
      width,
      height,
      tagLabel,
      tagColor,
    } = props;
    const icon = React.createElement(iconElement, {
      width: width || 32,
      height: height || 32,
      className: styles.dialogItemIcon,
    });
    return (
      <div
        className={classNames(styles.dialogItem, {
          [styles.dialogItemDisabled]: disableReason,
        })}
        onClick={disableReason ? undefined : onClick}
      >
        {tagLabel && (
          <>
            <TagIconBackground
              color={tagColor || '#7CB305'}
              className={styles.tagIcon}
            />
            <span className={styles.tagLabel}>{tagLabel}</span>
          </>
        )}

        {icon}
        <div className={styles.dialogItemTitle}>{name}</div>
        <div
          className={classNames(styles.dialogItemDescription, {
            [styles.bottomMargin]: isConnected === false,
          })}
        >
          {description}
        </div>
        {isConnected === false ? (
          <div className={styles.dialogItemButton}>Activate</div>
        ) : (
          <div className={styles.dialogItemButtonSecondary}>
            Choose <ArrowRightOutlined />
          </div>
        )}
        {disableReason && (
          <div className={styles.dialogItemDisableReasonContainer}>
            <div className={styles.dialogItemDisableReason}>
              {disableReason}
            </div>
          </div>
        )}
      </div>
    );
  },
);

export const TemplateGoButtonOld: FunctionComponent<Props> = props => {
  const {
    label = 'Create',
    templateName = DEFAULT_TEMPLATE_NAME,
    type,
    css,
    leftIcon,
    onClick,
  } = props;
  const template = AUTOMATION_TEMPLATES[templateName];

  const { setActiveTemplate, activeTemplate } =
    AutomationTemplateContainer.useContainer();
  const { twilio: twilioFeatFlag } = useSelector(companyFeatureFlagsSelector);
  const modalActions = useModalActions();
  const twilioAvailableFlag = useSelector(getTwilioAvailable);
  const isSmsSupported = twilioFeatFlag || twilioAvailableFlag;
  const [templateChannel, setTemplateChannel] =
    useState<AutomationTemplateChannel>('facebook');
  const [dialogOpen, setDialogOpen] = useState(false);
  const automationCRUDActions = useAutomationsCRUDActions();
  const { createAutomation } = useAutomations();
  const facebookPage = useSelector(activeFacebookPageSelector);
  const botId = useSelector(activeChatbotIdSelector);
  const [activeStep, setActiveStep] = useState(1);
  const [isOutboundAutomation, setIsOutboundAutomation] = useState(false);
  const [outboundAutomationParams, setOutboundAutomationParams] =
    useState<any>(undefined);
  const history = useHistory();

  const setAutomationTypeAndStep = (isOutbound: boolean, step: number) => {
    setIsOutboundAutomation(isOutbound);
    setActiveStep(step);
  };

  const reset = () => {
    setAutomationTypeAndStep(false, 1);
    setOutboundAutomationParams(undefined);
    setDialogOpen(false);
  };

  const {
    connectChannel,
    isSMSConnected,
    isFacebookConnected,
    isInstagramConnected,
    isEmailConnected,
  } = useChannelConnection(() => {
    setAutomationTypeAndStep(false, activeStep + 1);
  });

  const onSkipTemplate = async () => {
    if (outboundAutomationParams) {
      await createAutomation(
        'famous_audience_extractor',
        outboundAutomationParams,
      );
      reset();
    }
    setActiveTemplate({
      template: AUTOMATION_TEMPLATES.famous_audience_extractor,
      channel: templateChannel,
    });
    setActiveStep(activeStep + 1);
  };
  const outboundItems = [
    {
      iconElement: () => <img src={XrayIconSrc} />,
      width: 232,
      height: 56,
      name: 'Unicorn X-Ray Pixel Vision',
      tagLabel: 'New',
      visible: true,
      description:
        'Detect the Email Addresses of your anonymous website visitors',
      onClick: () => {
        setAutomationTypeAndStep(true, activeStep + 1);
        setOutboundAutomationParams({
          inputTriggers: [{ kind: 'getemails' }],
          unicornOnly: true,
          unicornEmailQuantity:
            template.maxFollowUpCounts.maxUnicornEmailQuantity,
        });
      },
    },
    {
      iconElement: UploadIcon,
      width: 232,
      height: 56,
      name: 'Upload a contact list',
      description: 'Upload a CSV file',
      visible: true,
      isConnected: isEmailConnected || isSMSConnected,
      onClick: () => {
        if (
          !isEmailConnected &&
          !isSMSConnected &&
          isSmsSupported &&
          facebookPage
        ) {
          modalActions.openConnectChannelModal({
            channels: ['sms', 'email'],
            facebookPage,
            title: 'Activate lists uploading',
            subTitle: 'Please choose a way you want to continue with',
          });
        } else {
          modalActions.openImportContactListModal({
            onSuccess: ({ audienceId }) => {
              setOutboundAutomationParams({
                inputTriggers: [{ kind: 'audience', audienceId }],
                unicornOnly: true,
                unicornSMSQuantity:
                  template.maxFollowUpCounts.maxUnicornSMSQuantity,
                unicornEmailQuantity:
                  template.maxFollowUpCounts.maxUnicornEmailQuantity,
              });
              setAutomationTypeAndStep(true, activeStep + 1);
            },
          });
        }
      },
    },
  ] as const;

  const channelsState = {
    facebook: {
      isConnected: isFacebookConnected,
      icon: FacebookIcon,
      name: 'Facebook',
      description:
        'Reply to Facebook Page messages, Post Comments, or Ads engagements',
    },
    instagram: {
      isConnected: isInstagramConnected,
      icon: InstagramIcon,
      name: 'Instagram',
      description: 'Reply to DMs, Post Comments, and Story interactions',
    },
  };

  const Channel = (channel: AutomationTemplateChannel) => {
    if (
      !template.channels[channel] ||
      !channelsState[channel] ||
      channel === 'webchat'
    ) {
      return null;
    }
    const { icon, name, description, isConnected } = channelsState[channel];
    const onChannelClick = () => {
      if (isConnected) {
        setTemplateChannel(channel);
        setAutomationTypeAndStep(false, activeStep + 1);
      } else {
        setTemplateChannel(channel);
        connectChannel(channel);
      }
    };
    return (
      <DialogItem
        iconElement={icon}
        name={name}
        description={description}
        isConnected={isConnected}
        onClick={onChannelClick}
      />
    );
  };

  const AutomationSource = React.memo(() => {
    return (
      <div className={styles.content}>
        <div
          className={classNames(styles.link, {
            [styles.blueLink]: activeStep === 1,
          })}
          onClick={() => {
            history.push(
              BotBuilderRoutes.automationHome.getLink({
                chatbotId: botId,
              }),
            );
            reset();
          }}
        >
          My Automations
        </div>
        <div className={styles.topWrapper}>
          <Heading size={2} className={styles.header}>
            Add new Automation
          </Heading>
          <div className={styles.dialogCaption}>
            Pick a lead source to get started. Don’t worry, you can add more
            later!
          </div>
          <div className={styles.dialogContent}>
            <div className={styles.dialogHeading}>Outbound Sales Outreach</div>
            <div className={styles.dialogSubheading}>
              Choose a data source to power your Outbound Sales Outreach
            </div>
            <div className={styles.dialogItems}>
              {outboundItems
                .filter(item => item.visible)
                .map(item => (
                  <DialogItem key={item.name} {...item} />
                ))}
            </div>
          </div>
        </div>
        <div className={styles.bottomWrapper}>
          <div className={styles.dialogContent}>
            <div className={styles.dialogHeading}>Inbound Lead Capture</div>
            <div className={styles.dialogSubheading}>
              Automated Email and SMS follow-ups triggered by events happening
              on other platforms
            </div>
            <div className={styles.dialogItems}>
              {Channel('instagram')}
              {Channel('facebook')}
              <DialogItem
                iconElement={WebchatIcon}
                name="Web Chat"
                description="Chat with leads visiting your website or grab their contact info for later"
                onClick={() => {
                  setAutomationTypeAndStep(false, activeStep + 1);
                  setTemplateChannel('webchat');
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  });

  const headerSteps = [
    {
      step: 1,
      title: 'Step 1. Choose Input Trigger',
    },
    {
      step: 2,
      title: 'Step 2. Choose a Template',
    },
    {
      step: 3,
      title: 'Step 3. Set up Automation',
    },
  ].filter(
    step => !isOutboundAutomation || (isOutboundAutomation && step.step !== 3),
  );

  useEffect(() => {
    if (!activeTemplate) {
      setDialogOpen(false);
      setActiveStep(1);
    }
  }, [activeTemplate]);

  useEffect(() => {
    if (botId && dialogOpen) {
      automationCRUDActions.getExistingInputTriggers(botId);
    }
  }, [botId, dialogOpen, automationCRUDActions]);

  return (
    <div className={classNames(styles.container, css?.container)}>
      <div
        className={classNames(
          styles.button,
          css?.button,
          styles[`button__${type}`],
        )}
        onClick={() => {
          onClick?.();
          setAutomationTypeAndStep(false, 1);
          setDialogOpen(true);
        }}
      >
        {leftIcon && leftIcon}
        {label}
        {!leftIcon && <ArrowRightOutlined />}
      </div>

      {dialogOpen && (
        <Overlay className={styles.overlay}>
          <div className={styles.dialog}>
            <CloseOutlined
              className={styles.dialogCloseButton}
              onClick={reset}
            />
            <div className={styles.headerContainer}>
              <div className={styles.headerContent}>
                <div
                  className={styles.back}
                  onClick={() => {
                    if (activeStep === 1) {
                      reset();
                    }
                    setActiveStep(activeStep - 1);
                    setOutboundAutomationParams(undefined);
                  }}
                >
                  <ArrowLeftOutlined />
                  Back
                </div>
                <div className={styles.steps}>
                  {headerSteps.map(item => {
                    return (
                      <React.Fragment key={item.step}>
                        <div
                          key={item.step}
                          className={classNames(styles.step, {
                            [styles.inactiveStep]: activeStep !== item.step,
                          })}
                        >
                          {activeStep > item.step && (
                            <CheckOutlined className={styles.checkmark} />
                          )}
                          {item.title}
                        </div>
                        {item.step !== headerSteps.length && (
                          <div className={styles.dashes} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
            {activeStep === 1 && <AutomationSource />}
            {activeStep === 2 && (
              <AutomationTemplates
                className={styles.templatesContent}
                heading={
                  <div className={styles.heading}>
                    <div className={styles.link} onClick={onSkipTemplate}>
                      Skip this step
                    </div>
                    Create an {isOutboundAutomation ? 'Outbound' : 'Inbound'}{' '}
                    Automation
                  </div>
                }
                setActiveTemplate={setActiveTemplate}
                templateChannel={templateChannel}
                activeStep={activeStep}
                setActiveStep={setActiveStep}
                outboundAutomationParams={outboundAutomationParams}
                closeModal={reset}
              />
            )}
            {!isOutboundAutomation && activeStep === 3 && <ActiveAutomation />}
          </div>
        </Overlay>
      )}
    </div>
  );
};

export const TemplateGoButton: React.FC<Props> = props => {
  const isAutomationTemplateFeatureEnabled = useSelector(
    getIsAutomationTemplateFeatureEnabled,
  );
  const history = useHistory();
  const chatbotId = useSelector(activeChatbotIdSelector);
  const { primaryButtonStyle } = useWhitelabelConfig();

  const { label = 'Create', type, css, leftIcon, onClick } = props;

  const navigateToNewAutomation = () => {
    history.push(BotBuilderRoutes.automationNew.getLink({ chatbotId }));
  };

  const isShopify = useSelector(selectIsShopifyContext);

  if (isShopify) {
    return <></>;
  }

  if (isAutomationTemplateFeatureEnabled) {
    return (
      <AutomationTemplateContainer.Provider>
        <TemplateGoButtonOld {...props} />
      </AutomationTemplateContainer.Provider>
    );
  }

  return (
    <div className={classNames(styles.container, css?.container)}>
      <div
        className={classNames(
          styles.button,
          css?.button,
          styles[`button__${type}`],
        )}
        onClick={() => {
          onClick?.();
          navigateToNewAutomation();
        }}
        style={primaryButtonStyle}
      >
        {leftIcon && leftIcon}
        {label}
        {!leftIcon && <ArrowRightOutlined />}
      </div>
    </div>
  );
};
